import { useState } from 'react';
import './ThemeToggle.css';

const KEY = 'usacow-theme';

export type Theme = 'dark' | 'light';

/** Apply the persisted theme as early as possible (call from an inline head script or
 *  at module load) to avoid a flash of the wrong theme. */
export function applyStoredTheme(): void {
  try {
    const saved = localStorage.getItem(KEY) as Theme | null;
    if (saved) document.documentElement.setAttribute('data-theme', saved);
  } catch {
    /* localStorage unavailable */
  }
}

const MoonIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
  </svg>
);

const SunIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);

/** Sun/moon theme toggle. Flips [data-theme] on <html> and persists to localStorage. */
export function ThemeToggle() {
  const get = (): Theme =>
    (document.documentElement.getAttribute('data-theme') as Theme | null) ?? 'dark';
  const [theme, setTheme] = useState<Theme>(get());

  const toggle = () => {
    const next: Theme = get() === 'light' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', next);
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* ignore */
    }
    setTheme(next);
  };

  const isLight = theme === 'light';
  return (
    <button
      className="theme-toggle"
      onClick={toggle}
      aria-label={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
      title={isLight ? 'Dark mode' : 'Light mode'}
    >
      {isLight ? <MoonIcon /> : <SunIcon />}
    </button>
  );
}
